import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosSeries } from '../../api/pos-types.js';
import { Chip, QueryState, type TabProps } from './common.js';
import { formatDateTime } from './format.js';

const TYPE_TEXT = { D: 'Invoices', C: 'Credit notes', E: 'Emergency' } as const;

export function SeriesTab({ orgId, outletId }: TabProps): JSX.Element {
  const series = useQuery({
    queryKey: ['pos-series', orgId, outletId],
    queryFn: () =>
      apiFetch<{ invoicePrefix: string | null; series: PosSeries[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/series`,
      ),
  });

  return (
    <section aria-labelledby="series-heading">
      <h2 id="series-heading">Invoice numbers</h2>
      <QueryState loading={series.isLoading} error={series.error} />
      {series.data ? (
        <>
          <p>
            Outlet code:{' '}
            {series.data.invoicePrefix ? (
              <code>{series.data.invoicePrefix}</code>
            ) : (
              <span className="muted">not set yet</span>
            )}
          </p>
          {series.data.series.length === 0 ? (
            <p className="muted">
              No till has been given invoice numbers yet. Numbering stays off until your accountant
              has approved the numbering rules.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="pos-table">
                <thead>
                  <tr>
                    <th scope="col">Series</th>
                    <th scope="col">Year</th>
                    <th scope="col">State</th>
                    <th scope="col">Used up to</th>
                    <th scope="col">Granted up to</th>
                    <th scope="col">Left</th>
                    <th scope="col">Closed at</th>
                    <th scope="col">Last grant</th>
                  </tr>
                </thead>
                <tbody>
                  {series.data.series.map((s) => (
                    <tr key={s.id}>
                      <th scope="row">
                        <code>{s.prefix ?? `?${s.seriesType}`}</code>
                        <div className="muted">{TYPE_TEXT[s.seriesType]}</div>
                      </th>
                      <td>{s.financialYear}</td>
                      <td>
                        <Chip tone={s.state === 'open' ? 'ok' : undefined}>{s.state}</Chip>
                      </td>
                      <td>{s.confirmedThrough}</td>
                      <td>{s.ceiling}</td>
                      <td>
                        {s.state === 'open' ? Math.max(0, s.ceiling - s.confirmedThrough) : '—'}
                      </td>
                      <td>{s.closeWatermark ?? '—'}</td>
                      <td>{formatDateTime(s.lastGrantAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
